/**
 * RAG DD Assistant Implementation
 *
 * Full RAG implementation with document retrieval and conversation memory.
 * Implements IDDAssistant interface for seamless switching with MVP.
 */

import { supabase } from '@/lib/supabase/client';
import { reportError } from '@/lib/errors';
import { track } from '@/lib/analytics';
import type {
  IDDAssistant,
  DDAssistantContext,
  DDAssistantResponse,
  DDAssistantMode,
  DDConversationSummary,
  DDSource,
  RAGConfig,
  DEFAULT_RAG_CONFIG,
} from './assistant.types';
import {
  searchDocumentChunks,
  hybridSearchDocuments,
  searchConversationHistory,
  createConversation,
  storeConversationMessage,
  getConversations,
  updateConversationSummary,
  assembleContext,
  type SearchResult,
  type HistorySearchResult,
} from './vector-store';

// ============================================
// RAG Assistant Class
// ============================================

/**
 * RAG implementation of DD Assistant.
 *
 * Characteristics:
 * - Retrieves relevant document chunks for context
 * - Maintains conversation history across sessions
 * - Provides source citations
 * - Higher quality but more expensive
 */
export class RAGAssistant implements IDDAssistant {
  private readonly mode: DDAssistantMode = 'rag';
  private readonly config: RAGConfig;
  private currentConversationId: string | null = null;

  constructor(config: Partial<RAGConfig> = {}) {
    this.config = {
      maxDocumentChunks: 8,
      maxHistoryItems: 5,
      relevanceThreshold: 0.7,
      includeCitations: true,
      summarizeHistory: true,
      ...config,
    };
  }

  /**
   * Send a message and get a response with RAG context.
   */
  async chat(context: DDAssistantContext): Promise<DDAssistantResponse> {
    const startTime = Date.now();

    try {
      // Resolve IDs — map mock IDs to real UUIDs for RAG lookup
      const isValidUUID = (id: string) =>
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

      // Mock-to-UUID mapping for test data (seeded documents use real startup profile UUIDs)
      const MOCK_STARTUP_MAP: Record<string, string> = {
        s1: '43ef086a-7095-4935-9262-fe56f9ecd530', // TechFlow AI
      };

      const resolvedStartupId = MOCK_STARTUP_MAP[context.startupId] || context.startupId;
      const resolvedInvestorId = context.investorId;
      const canRetrieve = isValidUUID(resolvedStartupId);

      let documentResults: SearchResult[] = [];
      let historyResults: HistorySearchResult[] = [];
      let queryEmbedding: number[] = [];

      if (canRetrieve) {
        // Step 1: Generate embedding for the query.
        // B19: on failure this is null (not a zero vector). Searching with a zero
        // vector produced NaN cosine / dimension-mismatch and silently returned no
        // matches; skip retrieval instead so the no-documents path handles it.
        const emb = await this.generateQueryEmbedding(context.message);

        if (emb && emb.length > 0) {
          queryEmbedding = emb;

          // Step 2: Retrieve relevant document chunks
          documentResults = await this.retrieveDocuments(
            queryEmbedding,
            resolvedStartupId,
            context.message
          );

          // Proof-ready: make the (client-side) RAG retrieval visible in
          // analytics_events — chunkCount>0 + topSimilarity prove a real vector
          // search ran against this startup's document_chunks (the edge fn only
          // ever sees a context string, so it cannot prove the retrieval itself).
          track('dd_rag_retrieval', {
            startupId: resolvedStartupId,
            chunkCount: documentResults.length,
            topSimilarity: documentResults[0]?.similarity ?? 0,
          });

          // Step 3: Retrieve relevant conversation history (only with valid investor UUID)
          if (isValidUUID(resolvedInvestorId)) {
            historyResults = await this.retrieveHistory(
              queryEmbedding,
              resolvedInvestorId,
              resolvedStartupId
            );
          }
        }
      }

      // Step 3.5: When no documents exist, do NOT inject fake data.
      // Instead, inform the LLM that no documents are available so it
      // can guide the investor toward requesting them.
      let noDocumentsNotice: string | null = null;

      if (documentResults.length === 0) {
        noDocumentsNotice = `IMPORTANT: No data room documents have been submitted for this startup yet. ` +
          `Do NOT fabricate or assume any financial figures, team details, or metrics. ` +
          `Instead, clearly state that no documents are available and suggest the investor: ` +
          `(1) request the startup to upload their pitch deck and data room, ` +
          `(2) use the messaging feature to ask the founder directly, ` +
          `(3) focus questions on what's available in the startup profile only.`;
      }

      // Step 4: Assemble context within token budget
      const assembledContext = assembleContext(
        documentResults,
        historyResults,
        4000 // Max retrieval tokens
      );

      // When no documents exist, add notice to context (no fake sources)
      if (noDocumentsNotice) {
        assembledContext.documentContext = noDocumentsNotice;
        // Do NOT add any sources — empty sources array signals "no documents"
      }

      // Step 5: Build the DATA-ONLY grounding context (server owns the prompt).
      const retrievedContext = this.buildDataContext(
        context.startupContext,
        assembledContext.documentContext,
        assembledContext.historyContext
      );

      // Step 6: Call LLM via edge function. B2: send excerpts as `retrievedContext`
      // DATA — never as `systemPrompt`/`startupContext` (SEC-DOC-03 rejects those).
      const response = await this.callRAGEndpoint({
        message: context.message,
        history: context.history || [],
        retrievedContext,
      });

      // Step 7: Store conversation (async, don't block) — only with valid UUIDs
      if (isValidUUID(resolvedInvestorId) && isValidUUID(resolvedStartupId)) {
        this.persistConversation(
          resolvedInvestorId,
          resolvedStartupId,
          context.message,
          response.message.content,
          queryEmbedding
        ).catch((err) => reportError(err, { context: 'RAGAssistant.query.persistConversation', level: 'warning', silent: true }));
      }

      const processingTimeMs = Date.now() - startTime;

      return {
        message: {
          ...response.message,
          metadata: {
            ...response.message.metadata,
            sources: assembledContext.sources.map((s) => s.label),
          },
        },
        sources: this.config.includeCitations ? assembledContext.sources : undefined,
        suggestedQuestions: response.suggestedQuestions,
        topicsDiscussed: response.topicsDiscussed,
        usage: {
          inputTokens: response.usage?.inputTokens || 0,
          outputTokens: response.usage?.outputTokens || 0,
          totalTokens: response.usage?.totalTokens || 0,
          retrievalTokens: assembledContext.totalTokens,
        },
        mode: this.mode,
        processingTimeMs,
      };
    } catch (error) {
      reportError(error, { context: 'RAGAssistant.query' });
      throw error;
    }
  }

  /**
   * Get conversation history for an investor-startup pair.
   */
  async getHistory(
    investorId: string,
    startupId: string
  ): Promise<DDConversationSummary[]> {
    const conversations = await getConversations(investorId, startupId);

    return conversations.map((c) => ({
      conversationId: c.id,
      startupId: c.startup_id,
      startupName: '', // Would need to join with startups table
      investorId: c.investor_user_id,
      summary: c.summary || 'No summary available',
      topics: c.topics || [],
      messageCount: c.message_count,
      totalTokens: c.total_tokens,
      createdAt: c.created_at,
      lastMessageAt: c.last_message_at,
    }));
  }

  /**
   * Clear conversation history.
   */
  async clearHistory(
    investorId: string,
    startupId: string,
    permanent: boolean = false
  ): Promise<void> {
    if (permanent) {
      // Delete from database
      const conversations = await getConversations(investorId, startupId);
      for (const conv of conversations) {
        await supabase.from('dd_conversations').delete().eq('id', conv.id);
      }
    }

    // Reset current conversation
    this.currentConversationId = null;
  }

  /**
   * Get the current mode identifier.
   */
  getMode(): DDAssistantMode {
    return this.mode;
  }

  // ============================================
  // Private Methods
  // ============================================

  /**
   * Generate embedding for query using edge function.
   */
  private async generateQueryEmbedding(text: string): Promise<number[] | null> {
    try {
      const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
      const { data: sessionData } = await supabase.auth.getSession();

      if (!sessionData?.session) {
        reportError(new Error('No session for embedding generation'), { context: 'RAGAssistant.generateQueryEmbedding', level: 'warning' });
        return null; // B19: no garbage zero-vector — caller skips retrieval
      }

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(`${supabaseUrl}/functions/v1/generate-embedding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session.access_token}`,
          'apikey': process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({ text }),
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (!response.ok) {
        reportError(new Error(`Embedding API error: ${response.status}`), { context: 'RAGAssistant.generateQueryEmbedding', level: 'warning' });
        return null; // B19: no garbage zero-vector — caller skips retrieval
      }

      const data = await response.json();
      return data.embedding;
    } catch (error) {
      reportError(error, { context: 'RAGAssistant.generateQueryEmbedding', level: 'warning' });
      return null; // B19: no garbage zero-vector — caller skips retrieval
    }
  }

  /**
   * Retrieve relevant document chunks.
   */
  private async retrieveDocuments(
    queryEmbedding: number[],
    startupId: string,
    queryText: string
  ): Promise<SearchResult[]> {
    // Use hybrid search for better results
    const results = await hybridSearchDocuments(
      queryEmbedding,
      queryText,
      startupId,
      {
        matchCount: this.config.maxDocumentChunks,
        semanticWeight: 0.7,
      }
    );

    // B3: filter on the SEMANTIC (cosine) score, which is the scale the 0.7
    // relevanceThreshold was calibrated for (and what the rest of the RAG code uses
    // as similarityThreshold). combined_score = cosine*0.7 + ts_rank_cd*0.3, and
    // ts_rank_cd is typically << 1, so filtering combined_score by 0.7 required
    // cosine ~= 1.0 and silently dropped almost every relevant chunk. combined_score
    // remains the RPC's ranking/order signal.
    return results.filter((r) => r.semantic_score >= this.config.relevanceThreshold);
  }

  /**
   * Retrieve relevant conversation history.
   */
  private async retrieveHistory(
    queryEmbedding: number[],
    investorId: string,
    startupId: string
  ): Promise<HistorySearchResult[]> {
    const results = await searchConversationHistory(
      queryEmbedding,
      investorId,
      startupId,
      {
        matchCount: this.config.maxHistoryItems,
        similarityThreshold: 0.6,
      }
    );

    return results;
  }

  /**
   * @deprecated Only used by MockAssistant. RAGAssistant no longer calls this.
   * Provides synthetic document context for mock/test mode ONLY.
   * NEVER use in production — fabricated citations violate DD integrity.
   */
  private getDemoFallback(startupContext: DDAssistantContext['startupContext']): {
    sources: DDSource[];
    documentContext: string;
  } {
    const name = startupContext?.name || 'Demo Startup';

    const sources: DDSource[] = [
      {
        type: 'document',
        label: 'Pitch Deck Q4 2024.pdf',
        snippet: `${name} provides enterprise-grade workflow automation powered by proprietary ML models, targeting SMBs with 50-500 employees.`,
        relevance: 0.92,
        location: 'Slides 3-5',
      },
      {
        type: 'document',
        label: 'Financial Projections 2025.xlsx',
        snippet: `Current MRR: $18,000 with 15% month-over-month growth. Projected ARR of $500K by Q4 2025, assuming current conversion rates hold.`,
        relevance: 0.88,
        location: 'Revenue Sheet',
      },
      {
        type: 'document',
        label: 'Team Bios.pdf',
        snippet: `Alex Chen, CEO — Previously founded DataSync (acquired by Salesforce in 2021). 12 years in enterprise software. Stanford CS.`,
        relevance: 0.85,
        location: 'Page 1',
      },
    ];

    const documentContext = sources
      .map((s) => `Source: ${s.label} (${s.location})\n${s.snippet}`)
      .join('\n\n---\n\n');

    return { sources, documentContext };
  }

  /**
   * B2: build the DATA-ONLY context blob sent to the server as `retrievedContext`.
   *
   * This deliberately contains NO role/citation/guideline INSTRUCTIONS — those are
   * server-controlled (RAG_SYSTEM_PROMPT in dd-chat-rag) per SEC-DOC-03. The client
   * may only supply grounding DATA (startup overview + numbered doc excerpts +
   * relevant prior discussion), which the server fences via wrapUserData(). The [N]
   * labels are kept so the server prompt's "numbered citation" instruction works.
   */
  private buildDataContext(
    startupContext: DDAssistantContext['startupContext'],
    documentContext: string,
    historyContext: string
  ): string {
    let ctx = `## Startup Overview
Name: ${startupContext.name}
Stage: ${startupContext.stage}
Industry: ${startupContext.industry}
${startupContext.description ? `Description: ${startupContext.description}` : ''}

## Investment Readiness Score
Overall: ${startupContext.scores?.finalScore || 'N/A'}/100
${this.formatScoreBreakdown(startupContext.scores)}
`;

    if (documentContext && !documentContext.startsWith('IMPORTANT: No data room')) {
      const numberedContext = this.numberDocumentSources(documentContext);
      ctx += `
## Relevant Documents (numbered excerpts from the data room — cite with [N])
${numberedContext}
`;
    } else if (documentContext) {
      ctx += `
## Data Room Status
${documentContext}
`;
    }

    if (historyContext) {
      ctx += `
## Previous Discussion (relevant past exchanges with this investor)
${historyContext}
`;
    }

    return ctx;
  }

  /**
   * Format score breakdown for prompt.
   */
  private formatScoreBreakdown(
    scores?: DDAssistantContext['startupContext']['scores']
  ): string {
    if (!scores) return '';

    const dimensions = [
      { key: 'team', label: 'Team' },
      { key: 'market', label: 'Market' },
      { key: 'product', label: 'Product' },
      { key: 'traction', label: 'Traction' },
      { key: 'financials', label: 'Financials' },
    ];

    return dimensions
      .filter((d) => scores[d.key as keyof typeof scores] !== undefined)
      .map((d) => `- ${d.label}: ${scores[d.key as keyof typeof scores]}`)
      .join('\n');
  }

  /**
   * Number document sources in the assembled context for citation references.
   * Splits on `\n\n---\n\n` chunk delimiters and prepends [N] labels.
   */
  private numberDocumentSources(documentContext: string): string {
    if (!documentContext.trim()) return documentContext;

    const chunks = documentContext.split('\n\n---\n\n');
    return chunks
      .map((chunk, index) => `[${index + 1}] ${chunk.trim()}`)
      .join('\n\n---\n\n');
  }

  /**
   * Call RAG-enhanced edge function.
   */
  private async callRAGEndpoint(params: {
    message: string;
    history: any[];
    retrievedContext: string;
  }): Promise<{
    message: any;
    suggestedQuestions: string[];
    topicsDiscussed: string[];
    usage?: { inputTokens: number; outputTokens: number; totalTokens: number };
  }> {
    const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
    const { data: sessionData } = await supabase.auth.getSession();

    if (!sessionData?.session) {
      throw new Error('Not authenticated. Please log in again.');
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 90000);

    const response = await fetch(`${supabaseUrl}/functions/v1/dd-chat-rag`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionData.session.access_token}`,
        'apikey': process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || '',
      },
      body: JSON.stringify(params),
      signal: controller.signal,
    });

    clearTimeout(timeout);

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || `RAG request failed (${response.status})`);
    }

    return {
      message: data.data.message,
      suggestedQuestions: data.data.suggestedQuestions || [],
      topicsDiscussed: data.data.topicsDiscussed || [],
      usage: data.usage,
    };
  }

  /**
   * Persist conversation to database.
   */
  private async persistConversation(
    investorId: string,
    startupId: string,
    userMessage: string,
    assistantMessage: string,
    userEmbedding: number[] | null
  ): Promise<void> {
    // Get or create conversation
    if (!this.currentConversationId) {
      const conv = await createConversation(investorId, startupId);
      if (conv) {
        this.currentConversationId = conv.id;
      } else {
        reportError(new Error('Failed to create conversation'), { context: 'RAGAssistant.persistConversation', level: 'warning' });
        return;
      }
    }

    // Store user message. B19: normalize an empty/failed embedding to null so we
    // never write a bad vector.
    await storeConversationMessage(
      this.currentConversationId,
      investorId,
      startupId,
      'user',
      userMessage,
      userEmbedding && userEmbedding.length > 0 ? userEmbedding : null,
      Math.ceil(userMessage.length / 4)
    );

    // Generate embedding for assistant message and store
    const assistantEmbedding = await this.generateQueryEmbedding(assistantMessage);
    await storeConversationMessage(
      this.currentConversationId,
      investorId,
      startupId,
      'assistant',
      assistantMessage,
      assistantEmbedding,
      Math.ceil(assistantMessage.length / 4)
    );
  }
}

/**
 * Factory function to create RAGAssistant.
 */
export function createRAGAssistant(config?: Partial<RAGConfig>): IDDAssistant {
  return new RAGAssistant(config);
}
